import { ERROE_EMBED, DIGIT_EMBED, FAKE_EMBED } from '../const';
import { cloneDeep } from 'lodash-es';
import Quill from '../quill/index';

/**
 * 富文本中涉及的数据类型：
 * json: datel，即json类型的数据，外部不需关心datel
 * jsonText: 序列化后的json数据
 * html: 字符串类型的html
 * text: 纯文本
 */

/**
 * ========== 类型转换 ==========
 */
export const createQuill = () => {
  const container = document.createElement('div');
  return new Quill(container);
};
// json ==> htmlText
export const jsonToHtml = (datel) => {
  const quill = createQuill();
  quill.setContents(dotJsonToQuill(datel));
  return quill.root.innerHTML;
};

// htmlText -> json
export const htmlToJson = (html) => {
  const quill = createQuill();
  return quill.clipboard.convert(html);
};

// json文本 ==> 纯文本
export const jsonTextToText = (jsonText) => {
  const quill = createQuill();
  const parsedJson = JSON.parse(jsonText);
  quill.setContents(dotJsonToQuill(parsedJson));
  return getQuillText(quill);
};

// 获取quill的纯文本，如果是占位符，则占一位空白
export const getTextWithDigit = (quill) => {
  const delta = quill.getContents();
  const text = delta
    .map(function (op) {
      if (isDigitEmbedForQuill(op)) {
        return ' ';
      }
      return op.insert;
    })
    .join('');

  return removeLastNewline(text);
};

/**
 * ========== 工具 ==========
 */
// 获取编辑器纯文本格式的内容 -- 之前过滤过旧版的占位符标记，改造之后和getContents()值一样
export const getQuillText = (quill) => {
  const delta = quill.getContents();

  // 拼接纯文本
  const text = delta
    .filter(function (op) {
      return typeof op.insert === 'string';
    })
    .map(function (op) {
      return op.insert;
    })
    .join('');

  return removeLastNewline(text);
};

// 获取编辑器json格式的内容
export const getQuillJson = (quill) => {
  const delta = quillJsonToDot(quill.getContents());
  // 过滤掉单独的\n标签
  return delta
    .map((item, index) => {
      if (index === delta.length - 1) {
        // 去行尾的\n
        item.insert = removeLastNewline(item.insert);
      }
      return item;
    })
    .filter((item) => item.insert !== '');
};

// 获取过滤掉错误标记的json内容
export const getJsonRemoveExtraMark = (quill) => {
  const filteredContents = getQuillJson(quill).map((item) => {
    if (isErrorEmbed(item)) {
      Object.values(ERROE_EMBED).some((embed) => {
        if (embed !== ERROE_EMBED.custom_error) {
          delete item.attributes[embed];
        }
      });
    }
    if (isFakeEmbed(item)) {
      Object.values(FAKE_EMBED).some((embed) => {
        delete item.attributes[embed];
      });
    }

    // 如果attributes没有内容了，直接删除掉attributes属性
    if (item.attributes && Object.keys(item.attributes).length === 0) {
      delete item.attributes;
    }
    return item;
  });

  return revisedDelta(filteredContents);
};

// 数据修正：低错等标记删除后，会存在空的[{insert: '1'}, {insert: '2'}]这种数据,此函数处理这种情况：[{insert: '12'}]
export const revisedDelta = (data) => {
  let result = [];
  let mergedItem = null;

  for (let item of data) {
    if (!item.attributes) {
      if (mergedItem) {
        mergedItem.insert += item.insert;
      } else {
        mergedItem = { ...item };
      }
    } else {
      if (mergedItem) {
        result.push(mergedItem);
        mergedItem = null;
      }
      result.push(item);
    }
  }

  if (mergedItem) {
    result.push(mergedItem);
  }

  return result;
};

//是否为 占位标签
export const isDigitEmbed = (deltaItem) => {
  const hasDigitEmbed = Object.values(DIGIT_EMBED).some((item) => {
    return deltaItem.attributes && deltaItem.attributes[item] !== undefined;
  });
  return hasDigitEmbed;
};

//是否为 占位标签 --- quill中的判定，这里后面新加的，结构和我们定的不一样，
export const isDigitEmbedForQuill = (deltaItem) => {
  const hasDigitEmbed = Object.values(DIGIT_EMBED).some((key) => {
    return typeof deltaItem.insert === 'object' && deltaItem.insert[key];
  });
  return hasDigitEmbed;
};

//是否为 错误标签
export const isErrorEmbed = (deltaItem) => {
  const hasErrorEmbed = Object.values(ERROE_EMBED).some((item) => {
    return deltaItem.attributes && deltaItem.attributes[item] !== undefined;
  });

  return hasErrorEmbed;
};

export const isFakeEmbed = (deltaItem) => {
  const hasFakeEmbed = Object.values(FAKE_EMBED).some((item) => {
    return deltaItem.attributes && deltaItem.attributes[item] !== undefined;
  });

  return hasFakeEmbed;
};

// dot协议和quill协议的互转：dot协议 --> quill协议
export const dotJsonToQuill = (delta) => {
  const deltaCopy = cloneDeep(delta);
  return deltaCopy.map((item) => {
    if (isDigitEmbed(item)) {
      Object.values(DIGIT_EMBED).forEach((key) => {
        if (item.attributes[key]) {
          item.attributes = item.attributes[key];
          item.insert = {
            [key]: { value: item.insert, ...item.attributes },
          };
        }
      });
    }
    return item;
  });
};
// dot协议和quill协议的互转：quill协议 --> dot协议
export const quillJsonToDot = (delta) => {
  return delta.map((item) => {
    if (isDigitEmbedForQuill(item)) {
      Object.values(DIGIT_EMBED).forEach((key) => {
        if (item.insert[key]) {
          const attrsCopy = {};
          Object.keys(item.attributes).forEach((attrKey) => {
            // bugfix: 错误embed中嵌套digit标记时，会报错
            if (typeof item.attributes[attrKey] !== 'object') {
              attrsCopy[attrKey] = item.attributes[attrKey];
            }
          });
          const value = attrsCopy.value;
          delete attrsCopy.value;
          item.attributes = {
            [key]: attrsCopy,
          };
          item.insert = value;
        }
      });
    }
    return item;
  });
};

// 去掉行尾最后一个\n
export const removeLastNewline = (str) => {
  return str.replace(/\n(?=\n*$)/, '');
};
